import { useState, useEffect } from 'react';
import { getHistory, AuditEntry } from '../api/adminApi';
import styles from './CountryHistoryModal.module.css';

const ACTION_LABELS: Record<string, { label: string; color: string }> = {
  INITIALIZATION: { label: 'Initialized', color: '#6366f1' },
  CONFIRMATION:   { label: 'Month confirmed', color: '#16a34a' },
  ADJUSTMENT:     { label: 'Date adjusted', color: '#f59e0b' },
  CONFIG_CHANGE:  { label: 'Config changed', color: '#64748b' },
  ENABLE:         { label: 'Enabled', color: '#0ea5e9' },
  DISABLE:        { label: 'Disabled', color: '#dc2626' },
};

function formatRelative(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const m = Math.floor(diff / 60_000);
  const h = Math.floor(diff / 3_600_000);
  const d = Math.floor(diff / 86_400_000);
  if (m < 1) return 'Just now';
  if (m < 60) return `${m}m ago`;
  if (h < 24) return `${h}h ago`;
  if (d < 7) return `${d}d ago`;
  return new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

interface Props {
  locationCode: string;
  locationName: string;
  onClose: () => void;
}

export default function CountryHistoryModal({ locationCode, locationName, onClose }: Props) {
  const [entries, setEntries] = useState<AuditEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    getHistory(locationCode)
      .then(setEntries)
      .catch(() => setError('Failed to load history.'))
      .finally(() => setLoading(false));
  }, [locationCode]);

  return (
    <div className={styles.overlay} onClick={onClose}>
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
        <div className={styles.modalHeader}>
          <div>
            <h2 className={styles.title}>History</h2>
            <p className={styles.subtitle}>{locationName}</p>
          </div>
          <button className={styles.closeBtn} onClick={onClose}>✕</button>
        </div>

        <div className={styles.body}>
          {loading && (
            <div className={styles.center}>
              <div className={styles.spinner} />
            </div>
          )}

          {error && <div className={styles.error}>{error}</div>}

          {!loading && !error && entries.length === 0 && (
            <div className={styles.empty}>
              <p>No recorded actions yet.</p>
              <p className={styles.emptyHint}>Actions will appear here as you confirm months, make adjustments, and update configuration.</p>
            </div>
          )}

          {!loading && entries.length > 0 && (
            <div className={styles.timeline}>
              {entries.map((entry) => {
                const meta = ACTION_LABELS[entry.actionType] ?? { label: entry.actionType, color: '#64748b' };
                return (
                  <div key={entry.id} className={styles.entry}>
                    <div className={styles.entryDot} style={{ background: meta.color }} />
                    <div className={styles.entryContent}>
                      <div className={styles.entryHeader}>
                        <span className={styles.actionLabel} style={{ color: meta.color }}>{meta.label}</span>
                        <span className={styles.timestamp}>{formatRelative(entry.timestamp)}</span>
                      </div>
                      {entry.newValue != null && (
                        <div className={styles.valueBlock}>
                          <pre className={styles.value}>{JSON.stringify(entry.newValue, null, 2)}</pre>
                        </div>
                      )}
                      {entry.previousValue != null && (
                        <div className={styles.prevBlock}>
                          <span className={styles.prevLabel}>Previous:</span>
                          <pre className={styles.value}>{JSON.stringify(entry.previousValue, null, 2)}</pre>
                        </div>
                      )}
                      {entry.note && (
                        <p className={styles.note}>{entry.note}</p>
                      )}
                      {entry.sources && entry.sources.length > 0 && (
                        <div className={styles.sources}>
                          {entry.sources.map((s, i) => (
                            <a key={i} href={s.url} target="_blank" rel="noopener noreferrer" className={styles.sourceLink}>
                              {s.language.toUpperCase()} {s.comment ? `· ${s.comment}` : ''}
                            </a>
                          ))}
                        </div>
                      )}
                      <span className={styles.fullTime}>
                        {new Date(entry.timestamp).toLocaleString('en-GB', {
                          day: '2-digit', month: 'short', year: 'numeric',
                          hour: '2-digit', minute: '2-digit',
                        })}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
